/**
 * useOllamaModels Hook
 *
 * Manages Ollama model discovery, installation status, and downloads.
 * Provides functionality to:
 * - List all models with installation status
 * - Check if a specific model is installed
 * - Download models with real-time progress tracking via SSE
 * - Clear download progress
 */

import { useState, useEffect, useCallback } from 'react';
import { buildApiUrl } from '../services/urlResolver';
import { ensureArray, ensureBoolean } from '../utils/apiNormalizers';
import { JSON_HEADERS } from '../utils/httpUtils';

import { handleError } from '../utils/errorHandler';

export interface OllamaModel {
  id: string;
  installed: boolean;
  size_gb: number;
  vram_gb: number;
  description: string;
}

export interface DownloadProgress {
  status: 'idle' | 'downloading' | 'finalizing' | 'completed' | 'error' | 'already_installed';
  progress: number;
  speed?: string;
  eta?: string;
  message?: string;
}

interface UseOllamaModelsReturn {
  /** List of all known Ollama models with installation status */
  models: OllamaModel[];
  /** Loading state for initial fetch */
  loading: boolean;
  /** Whether Ollama CLI is available on the system */
  ollamaAvailable: boolean;
  /** Download progress for each model being downloaded */
  downloadProgress: Record<string, DownloadProgress>;
  /** Check if a specific model is installed */
  isModelInstalled: (modelId: string) => boolean;
  /** Get detailed info for a specific model */
  getModelInfo: (modelId: string) => OllamaModel | undefined;
  /** Start downloading a model (returns promise that resolves when done) */
  downloadModel: (modelId: string) => Promise<boolean>;
  /** Delete an installed model */
  deleteModel: (modelId: string) => Promise<boolean>;
  /** Clear download progress for a model */
  clearProgress: (modelId: string) => void;
  /** Refresh the models list from API */
  refreshModels: () => Promise<void>;
}

export function useOllamaModels(): UseOllamaModelsReturn {
  const [models, setModels] = useState<OllamaModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [ollamaAvailable, setOllamaAvailable] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<Record<string, DownloadProgress>>({});

  // Check Ollama availability
  const checkOllamaStatus = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/ollama/status'));
      const result = await response.json();
      const available = ensureBoolean(result.data?.available, false);
      setOllamaAvailable(available);
      return available;
    } catch (error) {
      console.error('[useOllamaModels] Failed to check Ollama status:', error);
      setOllamaAvailable(false);
      return false;
    }
  }, []);

  // Fetch all models with installation status
  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/api/v1/ollama/models'));
      const result = await response.json();

      if (result.success && result.data?.models) {
        // Defensive: ensure models is always an array
        setModels(ensureArray(result.data.models));
      } else {
        console.warn('[useOllamaModels] Unexpected response format:', result);
        setModels([]);
      }
    } catch (error) {
      console.error('[useOllamaModels] Failed to fetch Ollama models:', error);
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const init = async () => {
      const available = await checkOllamaStatus();
      if (available) {
        await fetchModels();
      } else {
        setLoading(false);
      }
    };
    init();
  }, [checkOllamaStatus, fetchModels]);

  // Check if specific model is installed
  const isModelInstalled = useCallback(
    (modelId: string): boolean => {
      const model = models.find((m) => m.id === modelId);
      return model?.installed ?? false;
    },
    [models]
  );

  // Get model info
  const getModelInfo = useCallback(
    (modelId: string): OllamaModel | undefined => {
      return models.find((m) => m.id === modelId);
    },
    [models]
  );

  // Download model with progress tracking via SSE
  const downloadModel = useCallback(
    async (modelId: string): Promise<boolean> => {
      // Initialize progress
      setDownloadProgress((prev) => ({
        ...prev,
        [modelId]: { status: 'downloading', progress: 0 },
      }));

      try {
        const response = await fetch(
          buildApiUrl(`/api/v1/ollama/models/${encodeURIComponent(modelId)}/pull`),
          {
            method: 'POST',
            headers: JSON_HEADERS,
          }
        );

        // Check if already installed (JSON response)
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const result = await response.json();
          if (result.data?.status === 'already_installed') {
            setDownloadProgress((prev) => ({
              ...prev,
              [modelId]: { status: 'already_installed', progress: 100 },
            }));
            return true;
          }
        }

        // Handle SSE stream
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body available');
        }

        let buffer = '';
        let success = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                setDownloadProgress((prev) => ({
                  ...prev,
                  [modelId]: {
                    status: data.status || 'downloading',
                    progress: data.progress ?? 0,
                    speed: data.speed,
                    eta: data.eta,
                    message: data.message,
                  },
                }));

                if (data.status === 'completed') {
                  success = true;
                  // Refresh models list to update installation status
                  await fetchModels();
                }

                if (data.status === 'error') {
                  console.error('[useOllamaModels] Download error:', data.message);
                  return false;
                }
              } catch (parseError) {
                console.warn('[useOllamaModels] Failed to parse SSE data:', line, parseError);
              }
            }
          }
        }

        return success;
      } catch (error) {
        console.error('[useOllamaModels] Download failed:', error);
        setDownloadProgress((prev) => ({
          ...prev,
          [modelId]: {
            status: 'error',
            progress: 0,
            message: handleError(error, 'OllamaModels', 'Download failed'),
          },
        }));
        return false;
      }
    },
    [fetchModels]
  );

  // Delete an installed model
  const deleteModel = useCallback(
    async (modelId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          buildApiUrl(`/api/v1/ollama/models/${encodeURIComponent(modelId)}`),
          { method: 'DELETE', headers: JSON_HEADERS }
        );
        const result = await response.json();

        if (response.ok && result.success) {
          await fetchModels();
          return true;
        }

        console.error('[useOllamaModels] Delete failed:', result);
        return false;
      } catch (error) {
        console.error('[useOllamaModels] Delete error:', error);
        return false;
      }
    },
    [fetchModels]
  );

  // Clear download progress for a model
  const clearProgress = useCallback((modelId: string) => {
    setDownloadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[modelId];
      return newProgress;
    });
  }, []);

  return {
    models,
    loading,
    ollamaAvailable,
    downloadProgress,
    isModelInstalled,
    getModelInfo,
    downloadModel,
    deleteModel,
    clearProgress,
    refreshModels: fetchModels,
  };
}
